import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { format, isValid } from 'date-fns';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: 'Helvetica'
  },
  header: {
    marginBottom: 20,
    textAlign: 'center'
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10
  },
  batchInfo: {
    marginBottom: 20
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 5
  },
  label: {
    width: '30%',
    fontWeight: 'bold'
  },
  value: {
    width: '70%'
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    marginTop: 10
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#bfbfbf',
    minHeight: 25
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold'
  },
  tableCell: {
    borderRightWidth: 1,
    borderRightColor: '#bfbfbf',
    padding: 5,
    fontSize: 10
  },
  lastCell: {
    borderRightWidth: 0
  },
  // Column widths
  sNo: { width: '8%' },
  indentId: { width: '15%' },
  projectName: { width: '25%' },
  location: { width: '20%' },
  createdDate: { width: '20%' },
  status: { width: '12%' }
});

const BatchIndentPDF = ({ batch }) => {
  const currentDateTime = format(new Date(), 'dd/MM/yyyy');

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'dd/MM/yyyy') : 'N/A';
  };

  const getLocation = (indent) => {
    // Check all possible location fields in order of preference
    if (indent.Location) return indent.Location;
    if (indent.ProjectLocation) return indent.ProjectLocation;
    if (indent.ProjectLocationName) return indent.ProjectLocationName;
    if (indent.LocationName) return indent.LocationName;
    if (indent.ProjectName) return indent.ProjectName; // Fallback to project name if no location is available
    return 'N/A';
  };

  const getStatus = (indent) => {
    return indent.Status || indent.ReceiptStatus || indent.IndentStatus || 'N/A';
  };

  const getCreatedDate = (indent) => {
    return indent.CreationDate || indent.CreatedDate || indent.CreatedAt || 'N/A';
  };

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Batch Indent Report</Text>
          <Text style={{ fontSize: 10 }}>Generated on: {currentDateTime}</Text>
        </View>

        <View style={styles.batchInfo}>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Batch Number:</Text>
            <Text style={styles.value}>{batch.BatchNumber || 'N/A'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Description:</Text>
            <Text style={styles.value}>{batch.Description || 'N/A'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Total Indents:</Text>
            <Text style={styles.value}>{batch.Indents?.length || 0}</Text>
          </View>
        </View>

        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={[styles.tableCell, styles.sNo]}>S.No</Text>
            <Text style={[styles.tableCell, styles.indentId]}>Indent ID</Text>
            <Text style={[styles.tableCell, styles.projectName]}>Project Name</Text>
            <Text style={[styles.tableCell, styles.location]}>Location</Text>
            <Text style={[styles.tableCell, styles.createdDate]}>Created Date</Text>
            <Text style={[styles.tableCell, styles.status, styles.lastCell]}>Status</Text>
          </View>

          {/* Table Rows */}
          {batch.Indents?.map((indent, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.sNo]}>{index + 1}</Text>
              <Text style={[styles.tableCell, styles.indentId]}>{indent.IndentID || 'N/A'}</Text>
              <Text style={[styles.tableCell, styles.projectName]}>{indent.ProjectName || 'N/A'}</Text>
              <Text style={[styles.tableCell, styles.location]}>{getLocation(indent)}</Text>
              <Text style={[styles.tableCell, styles.createdDate]}>{formatDate(getCreatedDate(indent))}</Text>
              <Text style={[styles.tableCell, styles.status, styles.lastCell]}>{getStatus(indent)}</Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default BatchIndentPDF; 